import React from 'react'
import { useStore } from '../../state/store'
import type { AnimationType } from '../../core/types'
import CollapsibleSection from './CollapsibleSection'

const AnimationPanel: React.FC = () => {
  const { settings, updateSettings } = useStore()

  const animationEnabled = settings.ditherAnimationEnabled || settings.dither2AnimationEnabled

  const animationTypes: { value: AnimationType; label: string; desc: string }[] = [
    { value: 'wave', label: 'Wave', desc: '字符沿正弦波偏移' },
    { value: 'pulse', label: 'Pulse', desc: '亮度和缩放脉动' },
    { value: 'jitter', label: 'Jitter', desc: '随机抖动' },
    { value: 'drift', label: 'Drift', desc: '缓慢漂移' },
    { value: 'breathe', label: 'Breathe', desc: '局部呼吸感' },
    { value: 'blink', label: 'Blink', desc: '随机闪烁' },
    { value: 'rainbow', label: 'Rainbow', desc: '循环色相变化' },
    { value: 'loop', label: 'Loop', desc: '沿路径循环移动' },
    { value: 'comet', label: 'Comet', desc: '带拖尾的高亮流星' },
    { value: 'audio', label: 'Audio', desc: '模拟音频律动' },
    { value: 'stream', label: 'Stream', desc: '倾斜字符流' },
    { value: 'none', label: 'None', desc: '仅保留鼠标交互' },
  ]

  const renderSlider = (
    label: string,
    value: number,
    min: number,
    max: number,
    step: number,
    onChange: (value: number) => void,
    formatter?: (value: number) => string,
  ) => (
    <div className="slider-container">
      <span className="slider-label">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1"
      />
      <span className="slider-value">{formatter ? formatter(value) : value}</span>
    </div>
  )

  return (
    <CollapsibleSection title="动画设置" icon="ANI" defaultExpanded={false}>
      <div className="space-y-3">
        <div className="xp-checkbox-row">
          <span className="xp-field-label">启用动画</span>
          <input
            type="checkbox"
            checked={animationEnabled}
            onChange={(e) => {
              updateSettings({
                ditherAnimationEnabled: e.target.checked,
                dither2AnimationEnabled: e.target.checked,
              })
            }}
          />
        </div>

        {animationEnabled && (
          <>
            <div>
              <label className="xp-group-label">动画类型</label>
              <div className="xp-grid-two">
                {animationTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => updateSettings({ animationType: type.value })}
                    className={`xp-btn justify-start min-h-[42px] px-2 py-1 text-left ${
                      settings.animationType === type.value ? 'xp-btn-pressed' : ''
                    }`}
                  >
                    <span>
                      <span className="block text-[16px] font-bold">{type.label}</span>
                      <span className="block text-[15px] xp-muted">{type.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {settings.animationType === 'wave' && (
              <div className="space-y-2">
                <div className="xp-group-label">Wave 参数</div>
                {renderSlider('振幅', settings.ditherWaveAmplitude, 0, 1, 0.05, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(2))}
                {renderSlider('频率', settings.ditherWaveFrequency, 0, 10, 0.5, (value) => {
                  updateSettings({ ditherWaveFrequency: value, dither2WaveFrequency: value })
                }, (value) => value.toFixed(1))}
                {renderSlider('速度', settings.ditherWaveSpeed, 0, 0.5, 0.01, (value) => {
                  updateSettings({ ditherWaveSpeed: value, dither2WaveSpeed: value })
                }, (value) => value.toFixed(2))}
              </div>
            )}

            {settings.animationType === 'pulse' && (
              <div className="space-y-2">
                <div className="xp-group-label">Pulse 参数</div>
                {renderSlider('强度', settings.ditherWaveAmplitude, 0, 1, 0.05, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(2))}
                {renderSlider('速度', settings.ditherWaveFrequency, 0, 5, 0.1, (value) => {
                  updateSettings({ ditherWaveFrequency: value, dither2WaveFrequency: value })
                }, (value) => value.toFixed(1))}
              </div>
            )}

            {settings.animationType === 'jitter' && (
              <div className="space-y-2">
                <div className="xp-group-label">Jitter 参数</div>
                {renderSlider('强度', settings.ditherWaveAmplitude, 0, 2, 0.1, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(1))}
              </div>
            )}

            {settings.animationType === 'drift' && (
              <div className="space-y-2">
                <div className="xp-group-label">Drift 参数</div>
                {renderSlider('幅度', settings.ditherWaveAmplitude, 0, 2, 0.1, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(1))}
                {renderSlider('速度', settings.ditherWaveSpeed, 0, 2, 0.1, (value) => {
                  updateSettings({ ditherWaveSpeed: value, dither2WaveSpeed: value })
                }, (value) => value.toFixed(1))}
              </div>
            )}

            {settings.animationType === 'breathe' && (
              <div className="space-y-2">
                <div className="xp-group-label">Breathe 参数</div>
                {renderSlider('强度', settings.ditherWaveAmplitude, 0, 1, 0.05, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(2))}
                {renderSlider('速度', settings.ditherWaveFrequency, 0.1, 2, 0.1, (value) => {
                  updateSettings({ ditherWaveFrequency: value, dither2WaveFrequency: value })
                }, (value) => value.toFixed(1))}
              </div>
            )}

            {settings.animationType === 'blink' && (
              <div className="space-y-2">
                <div className="xp-group-label">Blink 参数</div>
                {renderSlider('概率', settings.ditherWaveAmplitude, 0.1, 1, 0.05, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(2))}
                {renderSlider('速度', settings.ditherWaveFrequency, 0.5, 5, 0.5, (value) => {
                  updateSettings({ ditherWaveFrequency: value, dither2WaveFrequency: value })
                }, (value) => value.toFixed(1))}
              </div>
            )}

            {settings.animationType === 'rainbow' && (
              <div className="space-y-2">
                <div className="xp-group-label">Rainbow 参数</div>
                {renderSlider('速度', settings.ditherWaveSpeed, 0.1, 3, 0.1, (value) => {
                  updateSettings({ ditherWaveSpeed: value, dither2WaveSpeed: value })
                }, (value) => value.toFixed(1))}
                {renderSlider('饱和度', settings.ditherWaveAmplitude, 0.3, 1, 0.05, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(2))}
              </div>
            )}

            {settings.animationType === 'loop' && (
              <div className="space-y-2">
                <div className="xp-group-label">Loop 参数</div>
                {renderSlider('半径', settings.ditherWaveAmplitude * 5, 1, 10, 0.5, (value) => {
                  const normalized = value / 5
                  updateSettings({ ditherWaveAmplitude: normalized, dither2WaveAmplitude: normalized })
                }, (value) => value.toFixed(1))}
                {renderSlider('速度', settings.ditherWaveSpeed, 0.1, 3, 0.1, (value) => {
                  updateSettings({ ditherWaveSpeed: value, dither2WaveSpeed: value })
                }, (value) => value.toFixed(1))}
              </div>
            )}

            {settings.animationType === 'comet' && (
              <div className="space-y-2">
                <div className="xp-group-label">Comet 参数</div>
                {renderSlider('亮度', settings.ditherWaveAmplitude, 0.1, 2, 0.1, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(1))}
                {renderSlider('速度', settings.ditherWaveSpeed, 0.2, 3, 0.1, (value) => {
                  updateSettings({ ditherWaveSpeed: value, dither2WaveSpeed: value })
                }, (value) => value.toFixed(1))}
              </div>
            )}

            {settings.animationType === 'audio' && (
              <div className="space-y-2">
                <div className="xp-group-label">Audio 参数</div>
                {renderSlider('幅度', settings.ditherWaveAmplitude, 0.1, 2, 0.1, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(1))}
                {renderSlider('速度', settings.ditherWaveFrequency, 0.5, 5, 0.5, (value) => {
                  updateSettings({ ditherWaveFrequency: value, dither2WaveFrequency: value })
                }, (value) => value.toFixed(1))}
              </div>
            )}

            {settings.animationType === 'stream' && (
              <div className="space-y-2">
                <div className="xp-group-label">Stream 参数</div>
                {renderSlider('速度', settings.ditherWaveSpeed, 0.2, 3, 0.1, (value) => {
                  updateSettings({ ditherWaveSpeed: value, dither2WaveSpeed: value })
                }, (value) => value.toFixed(1))}
                {renderSlider('亮度', settings.ditherWaveAmplitude, 0.2, 1.5, 0.1, (value) => {
                  updateSettings({ ditherWaveAmplitude: value, dither2WaveAmplitude: value })
                }, (value) => value.toFixed(1))}
              </div>
            )}

            <div className="space-y-2">
              <div className="xp-group-label">鼠标交互</div>
              <div className="xp-checkbox-row">
                <span className="xp-field-label">启用交互</span>
                <input
                  type="checkbox"
                  checked={settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled}
                  onChange={(e) => {
                    updateSettings({
                      ditherMouseInteractionEnabled: e.target.checked,
                      dither2MouseInteractionEnabled: e.target.checked,
                    })
                  }}
                />
              </div>

              {(settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled) && (
                <>
                  <div>
                    <label className="xp-group-label">交互模式</label>
                    <select
                      value={settings.mouseInteractionMode}
                      onChange={(e) => updateSettings({ mouseInteractionMode: e.target.value as 'attract' | 'push' })}
                      className="xp-select"
                    >
                      <option value="attract">吸引</option>
                      <option value="push">推开</option>
                    </select>
                  </div>
                  {renderSlider('影响半径', settings.ditherMouseRadius, 0.1, 2, 0.1, (value) => {
                    updateSettings({ ditherMouseRadius: value, dither2MouseRadius: value })
                  }, (value) => value.toFixed(1))}
                  {renderSlider('交互强度', settings.hoverStrength, 10, 60, 2, (value) => {
                    updateSettings({ hoverStrength: value })
                  })}
                </>
              )}
            </div>

            <div className="xp-note">
              将鼠标移动到预览画布上，可以观察当前动画和交互效果。
            </div>
          </>
        )}
      </div>
    </CollapsibleSection>
  )
}

export default AnimationPanel
